import React, { useState, useEffect } from 'react';
import axios from 'axios';


const API_URL = process.env.REACT_APP_API_URL;
const ProfileEditModal = ({ 
  showModal, 
  onClose, 
  user, 
  profileData, 
  setProfileData, 
  occupations, 
  annualIncomes,
  onProfileUpdate 
}) => {
  const [localProfileData, setLocalProfileData] = useState({
    currentPassword: '',
    newPassword: '',
    name: '',
    birthday: '',
    address: '',
    photoLink: '',
    occupation: '',
    annual_income: ''
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [videoRef, setVideoRef] = useState(null);
  const [canvasRef, setCanvasRef] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize local state when modal opens or profileData changes
  useEffect(() => {
    if (showModal && profileData) {
      setLocalProfileData({
        currentPassword: '',
        newPassword: '',
        name: profileData.name || '',
        birthday: profileData.birthday || '',
        address: profileData.address || '',
        photoLink: profileData.photoLink || '',
        occupation: profileData.occupation || '',
        annual_income: profileData.annual_income || ''
      });
    }
  }, [showModal, profileData]);

  // Reset form when modal closes
  useEffect(() => {
    if (!showModal) {
      setLocalProfileData({
        currentPassword: '',
        newPassword: '',
        name: '',
        birthday: '',
        address: '',
        photoLink: '',
        occupation: '',
        annual_income: ''
      });
      setPhotoFile(null);
    }
  }, [showModal]);


  const compressImage = (dataUrl, maxWidth = 2048, maxHeight = 2048, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio;
          height *= ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        
        resolve(compressedDataUrl);
      };
      img.src = dataUrl;
    });
  };

  const uploadPhoto = async (photoDataString) => {
    try {
      setUploadingPhoto(true);
      
      // Only update local state - don't save to database yet
      setLocalProfileData(prev => ({ ...prev, photoLink: { photodatas: photoDataString } }));
      
      alert('プロフィール写真が選択されました。保存ボタンをクリックして確定してください。');
    } catch (error) {
      console.error('Error processing photo:', error);
      alert('写真の処理に失敗しました');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const deletePhoto = async () => {
    try {
      // Only update local state - don't delete from database yet
      setLocalProfileData(prev => ({ ...prev, photoLink: null }));
      
      alert('プロフィール写真の削除が選択されました。保存ボタンをクリックして確定してください。');
    } catch (error) {
      console.error('Error processing photo deletion:', error);
      alert('写真の削除処理に失敗しました');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check if it's an image file
      if (!file.type.startsWith('image/')) {
        alert('画像ファイルを選択してください。');
        event.target.value = '';
        return;
      }

      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          // Compress the image before setting it
          const compressedImage = await compressImage(e.target.result, 2048, 2048, 0.8);
          // Upload the compressed image data
          await uploadPhoto(compressedImage);
        } catch (error) {
          alert('画像の処理に失敗しました。別のファイルを試してください。');
          event.target.value = '';
          setPhotoFile(null);
        }
      };
      reader.onerror = () => {
        alert('ファイルの読み込みに失敗しました。別のファイルを試してください。');
        event.target.value = '';
        setPhotoFile(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setCameraStream(stream);
      setShowCamera(true);
      if (videoRef) {
        videoRef.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('カメラへのアクセスに失敗しました');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const takePhoto = async () => {
    if (videoRef && canvasRef) {
      const context = canvasRef.getContext('2d');
      
      // Get video dimensions
      const videoWidth = videoRef.videoWidth;
      const videoHeight = videoRef.videoHeight;
      
      // Check if video dimensions exceed 2048x2048
      if (videoWidth > 2048 || videoHeight > 2048) {
        alert('カメラの解像度が高すぎます。別のカメラまたは設定を試してください。');
        stopCamera();
        return;
      }
      
      canvasRef.width = videoWidth;
      canvasRef.height = videoHeight;
      context.drawImage(videoRef, 0, 0);
      
      const photoData = canvasRef.toDataURL('image/jpeg');
      
      try {
        // Compress the camera photo
        const compressedPhoto = await compressImage(photoData, 2048, 2048, 0.8);
        // Upload the compressed photo data
        await uploadPhoto(compressedPhoto);
      } catch (error) {
        alert('写真の処理に失敗しました。もう一度試してください。');
      }
      
      stopCamera();
    }
  };

  const handleProfileUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      const updateData = { ...localProfileData };
      
      // Remove empty password fields and photoLink (handled separately)
      if (!updateData.currentPassword) {
        delete updateData.currentPassword;
      }
      if (!updateData.newPassword) {
        delete updateData.newPassword;
      }
      delete updateData.photoLink; // Photo updates are handled separately

      if(!updateData.name){
        return alert("名前を入力してください。!");
      } 

      // Handle photo upload/delete first
      if (localProfileData.photoLink && localProfileData.photoLink.photodatas) {
        // New photo uploaded - save to database
        try {
          const photoResponse = await axios.post(`${API_URL}/api/photo-data/photo-data`, {
            photodatas: localProfileData.photoLink.photodatas
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Update the profile data with the new photo
          if (photoResponse.data.user) {
            updateData.photoLink = photoResponse.data.user.photoLink;
          }
        } catch (error) {
          console.error('Error uploading photo:', error);
          alert('写真のアップロードに失敗しました。プロフィールの更新を続行しますか？');
          // Continue with profile update even if photo fails
        }
      } else if (profileData.photoLink && !localProfileData.photoLink) {
        // Photo was deleted - remove from database
        try {
          await axios.delete(`${API_URL}/api/photo-data/photo-data`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          console.error('Error deleting photo:', error);
          alert('写真の削除に失敗しました。プロフィールの更新を続行しますか？');
          // Continue with profile update even if photo deletion fails
        }
      }

      // Update profile data
      const response = await axios.put(`${API_URL}/api/auth/profile`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update parent component
      onProfileUpdate(response.data);
      
      // Trigger a refresh for the Dashboard component
      window.dispatchEvent(new CustomEvent('profileUpdated'));
      
      onClose();
      // Reset password fields
      setLocalProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: ''
      }));
      alert('プロフィールが更新されました');
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('プロフィールの更新に失敗しました');
      }
    }
  };

  const handleCancel = () => {
    // Reset local state to original profile data
    if (profileData) {
      setLocalProfileData({
        currentPassword: '',
        newPassword: '',
        name: profileData.name || '',
        birthday: profileData.birthday || '',
        address: profileData.address || '',
        photoLink: profileData.photoLink || '',
        occupation: profileData.occupation || '',
        annual_income: profileData.annual_income || ''
      });
    }
    onClose();
  };

  if (!showModal) return null;

  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: isMobile ? '20px' : '30px',
          borderRadius: '10px',
          width: isMobile ? '90vw' : '500px',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <h2 style={{ 
            marginBottom: '20px',
            fontSize: isMobile ? '18px' : '20px'
          }}>プロフィール編集</h2>
          <div style={{ marginBottom: '15px' }}>
            
            <label style={{ display: 'block', marginBottom: '5px' }}>プロフィール写真</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'space-between' }}>
              
              <div>
                <div style={{ 
                  fontSize: isMobile ? '10px' : '12px', 
                  color: '#666', 
                  marginBottom: '10px',
                  fontStyle: 'italic'
                }}>
                  最大サイズ: 2048x2048ピクセル (画像ファイルのみ)
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '8px' : '10px', 
                  marginBottom: '10px',
                  alignItems: isMobile ? 'stretch' : 'center'
                }}>
                
                  <button
                    onClick={startCamera}
                    disabled={uploadingPhoto}
                    style={{
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      padding: isMobile ? '6px 12px' : '8px 16px',
                      borderRadius: '5px',
                      cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                      opacity: uploadingPhoto ? 0.6 : 1,
                      fontSize: isMobile ? '12px' : '14px'
                    }}
                  >
                    {uploadingPhoto ? 'アップロード中...' : 'カメラで撮影'}
                  </button>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    id="photo-upload"
                    disabled={uploadingPhoto}
                  />
                  <label
                    htmlFor="photo-upload"
                    style={{
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      padding: isMobile ? '6px 12px' : '8px 16px',
                      borderRadius: '5px',
                      cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                      display: 'inline-block',
                      opacity: uploadingPhoto ? 0.6 : 1,
                      fontSize: isMobile ? '12px' : '14px',
                      textAlign: 'center'
                    }}
                  >
                    {uploadingPhoto ? 'アップロード中...' : 'ファイルから選択'}
                  </label>
                  {(localProfileData.photoLink && (localProfileData.photoLink.photodatas || localProfileData.photoLink)) && (
                    <button
                      onClick={deletePhoto}
                      disabled={uploadingPhoto}
                      style={{
                        backgroundColor: '#f44336',
                        color: 'white',
                        border: 'none',
                        padding: isMobile ? '6px 12px' : '8px 16px',
                        borderRadius: '5px',
                        cursor: uploadingPhoto ? 'not-allowed' : 'pointer',
                        opacity: uploadingPhoto ? 0.6 : 1,
                        fontSize: isMobile ? '12px' : '14px'
                      }}
                    >
                      削除
                    </button>
                  )}
                </div>
              </div>
              <div>
                {localProfileData.photoLink && (
                  <img 
                    src={localProfileData.photoLink.photodatas || localProfileData.photoLink} 
                    alt="Profile" 
                    style={{ 
                      width: '130px', 
                      height: '150px', 
                      borderRadius: '10%',
                      objectFit: 'cover',
                      border: '2px solid #ddd'
                    }} 
                  />
                )}
              </div>
            </div>
            
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>現在のパスワード (変更する場合のみ入力)</label>
            <input
              type="password"
              value={localProfileData.currentPassword}
              onChange={(e) => setLocalProfileData(prev => ({ ...prev, currentPassword: e.target.value }))}
              style={{ width: '96%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
              placeholder="現在のパスワードを入力"
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>新しいパスワード</label>
            <input
              type="password"
              value={localProfileData.newPassword}
              onChange={(e) => setLocalProfileData(prev => ({ ...prev, newPassword: e.target.value }))}
              style={{ width: '96%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
              placeholder="新しいパスワードを入力"
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>名前</label>
            <input
              type="text"
              value={localProfileData.name}
              onChange={(e) => setLocalProfileData(prev => ({ ...prev, name: e.target.value }))}
              style={{ width: '96%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
            />
          </div>

          {/* <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>誕生日</label>
            <input
              type="date"
              value={profileData.birthday}
              onChange={(e) => setProfileData(prev => ({ ...prev, birthday: e.target.value }))}
              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>住所</label>
            <input
              type="text"
              value={profileData.address}
              onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>職業</label>
            <select
              value={profileData.occupation}
              onChange={(e) => setProfileData(prev => ({ ...prev, occupation: e.target.value }))}
              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
            >
              <option value="">選択してください</option>
              {occupations.map(occ => (
                <option key={occ._id} value={occ._id}>{occ.occupaionName}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px' }}>年収</label>
            <select
              value={profileData.annual_income}
              onChange={(e) => setProfileData(prev => ({ ...prev, annual_income: e.target.value }))}
              style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
            >
              <option value="">選択してください</option>
              {annualIncomes.map(income => (
                <option key={income._id} value={income._id}>{income.annualIncome}</option>
              ))}
            </select>
          </div> */}

          

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={handleCancel}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: isMobile ? '8px 16px' : '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: isMobile ? '12px' : '14px'
              }}
            >
              キャンセル
            </button>
            <button
              onClick={handleProfileUpdate}
              style={{
                backgroundColor: '#ff6b35',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              保存
            </button>
          </div>
        </div>
      </div>

      {/* Camera Modal */}
      {showCamera && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            <h3>カメラで撮影</h3>
            <video
              ref={setVideoRef}
              autoPlay
              style={{ width: '400px', height: '300px', marginBottom: '10px' }}
            />
            <canvas
              ref={setCanvasRef}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={takePhoto}
                style={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                撮影
              </button>
              <button
                onClick={stopCamera}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileEditModal; 